import { Link } from 'react-router-dom';
import { Pencil, Trash2, ExternalLink, Phone, Hammer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn, formatCents, formatDate } from '@/lib/utils';
import { DetailField, EmptyValue } from '@/components/SectionCard';
import { COUNTERTOP_JOB_STATUS_BADGE } from '@/lib/badges';
import type { CountertopJobFull } from '@/lib/api-types';

/**
 * Compact job-tracking strip across the top of the detail page: identity + status
 * on one line, then a tinted, scannable stat row (customer, phone, site, supplier,
 * dates, margin). Replaces the old tall header stack.
 */
export function CountertopTrackingBar({
  job,
  margin,
  editable,
  isAdmin,
  editHref,
  onDelete,
}: {
  job: CountertopJobFull;
  margin: number | null;
  editable: boolean;
  isAdmin: boolean;
  editHref: string;
  onDelete: () => void;
}) {
  const badge = COUNTERTOP_JOB_STATUS_BADGE[job.status];

  return (
    <section className="overflow-hidden rounded-lg border bg-card">
      {/* Identity + admin actions */}
      <div className="flex flex-wrap items-start justify-between gap-4 px-5 py-4">
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Countertop Job
          </p>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight tabular-nums">{job.number}</h1>
            <Badge variant={badge.variant}>{badge.label}</Badge>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {editable && (
            <Link to={editHref}>
              <Button variant="outline" size="sm">
                <Pencil className="mr-1.5 h-3.5 w-3.5" />
                Edit
              </Button>
            </Link>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Scannable stat strip */}
      <dl className="grid grid-cols-2 gap-x-5 gap-y-3.5 border-t bg-muted/30 px-5 py-3.5 sm:grid-cols-3 lg:grid-cols-6">
        <DetailField label="Customer">
          <Link
            to={`/customers/${job.customer_id}`}
            className="inline-flex max-w-full items-center gap-1 font-medium text-foreground hover:underline"
          >
            <span className="truncate">{job.customer_name}</span>
            <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
          </Link>
        </DetailField>

        {job.customer_phone && (
          <DetailField label="Phone">
            <a
              href={`tel:${job.customer_phone}`}
              className="inline-flex items-center gap-1 tabular-nums text-foreground hover:underline"
            >
              <Phone className="h-3 w-3 shrink-0 text-muted-foreground" />
              {job.customer_phone}
            </a>
          </DetailField>
        )}

        <DetailField label="Site address" className="col-span-2">
          {job.site_address ? (
            <span className="line-clamp-2" title={job.site_address}>
              {job.site_address}
            </span>
          ) : (
            <EmptyValue />
          )}
        </DetailField>

        <DetailField label="Install supplier">
          {job.supplier_name ? (
            job.supplier_id ? (
              <Link
                to={`/suppliers/${job.supplier_id}`}
                className="inline-flex max-w-full items-center gap-1 text-foreground hover:underline"
              >
                <Hammer className="h-3 w-3 shrink-0 text-muted-foreground" />
                <span className="truncate">{job.supplier_name}</span>
              </Link>
            ) : (
              <span className="inline-flex max-w-full items-center gap-1">
                <Hammer className="h-3 w-3 shrink-0 text-muted-foreground" />
                <span className="truncate">{job.supplier_name}</span>
              </span>
            )
          ) : (
            <EmptyValue />
          )}
        </DetailField>

        <DetailField label="Measurement">
          {job.measurement_date ? (
            <span className="tabular-nums">{formatDate(job.measurement_date)}</span>
          ) : (
            <EmptyValue />
          )}
        </DetailField>

        <DetailField label="Install">
          {job.install_date ? (
            <span className="tabular-nums">{formatDate(job.install_date)}</span>
          ) : (
            <EmptyValue />
          )}
        </DetailField>

        {margin != null && (
          <DetailField label="Est. margin">
            <span
              className={cn(
                'font-semibold tabular-nums',
                margin >= 0 ? 'text-green-700' : 'text-destructive',
              )}
            >
              {formatCents(margin)}
            </span>
          </DetailField>
        )}
      </dl>
    </section>
  );
}
